import { useState, useEffect, useCallback } from 'react';
import { fetchRestaurantStatus } from '../services/businessHours.js';

/**
 * Hook para verificar status de funcionamento do restaurante
 * @param {number} checkIntervalMs - Intervalo de verificação em milissegundos (padrão: 60000 = 1 minuto)
 * @returns {Object} - { isOpen, proximaAbertura, horariosFuncionamento, loading, error, checkNow }
 */
export const useBusinessHours = (checkIntervalMs = 60000) => {
    const [status, setStatus] = useState({
        isOpen: true, // Começa como aberto para evitar flash
        proximaAbertura: null,
        horariosFuncionamento: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const checkStatus = useCallback(async () => {
        try {
            setError(null);
            const result = await fetchRestaurantStatus();
            
            setStatus(prevStatus => {
                // Log apenas se houve mudança
                if (prevStatus.isOpen !== result.isAberto) {
                    console.log('Status do restaurante mudou:', {
                        de: prevStatus.isOpen ? 'ABERTO' : 'FECHADO',
                        para: result.isAberto ? 'ABERTO' : 'FECHADO',
                        proximaAbertura: result.proximaAbertura
                    });
                }
                
                return {
                    isOpen: result.isAberto,
                    proximaAbertura: result.proximaAbertura,
                    horariosFuncionamento: result.horariosFuncionamento
                };
            });
        } catch (err) {
            console.error('Erro ao verificar status do restaurante:', err);
            setError(err.message);
            // Em caso de erro, manter como aberto
            setStatus(prevStatus => ({ ...prevStatus, isOpen: true }));
        } finally {
            setLoading(false);
        }
    }, []);

    // Verificação inicial
    useEffect(() => {
        checkStatus();
    }, [checkStatus]);

    // Verificação periódica (a cada minuto)
    useEffect(() => {
        if (checkIntervalMs <= 0) return;

        const interval = setInterval(() => {
            console.log('Verificação automática de horário...');
            checkStatus();
        }, checkIntervalMs);
        
        return () => clearInterval(interval);
    }, [checkStatus, checkIntervalMs]);

    // Verificação quando a aba volta ao foco
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                console.log('Aba voltou ao foco, verificando status...');
                checkStatus();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [checkStatus]);

    return {
        ...status,
        loading,
        error,
        checkNow: checkStatus
    };
};
